import { Component } from 'react';
import type { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

interface ErrorBoundaryState {
  error: Error | null;
}

export interface ErrorBoundaryProps extends PropsWithChildren {
  fallback?: ReactNode;
}

/**
 * Catches render-time exceptions so a bug in one widget does not blank the app.
 *
 * Still a class component: React exposes no hook equivalent of
 * `componentDidCatch`, and this is the one place where that matters.
 *
 * Note this only covers *render* errors. Async failures are handled by React
 * Query plus `ErrorState`, which is why the two mechanisms coexist.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Replace with the real reporter (Sentry, etc.) when one is wired up.
    console.error('Errore non gestito nella UI:', error, info.componentStack);
  }

  private readonly handleReload = (): void => {
    // A full reload is the honest recovery: component state is unknown at this
    // point, and pretending a re-render fixes it tends to loop.
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (!error) return children;
    if (fallback) return fallback;

    return (
      <Box
        role="alert"
        sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 4, backgroundColor: 'background.default' }}
      >
        <Card sx={{ maxWidth: 480, p: 7, textAlign: 'center' }}>
          <Box
            aria-hidden="true"
            sx={(theme) => ({
              width: 56,
              height: 56,
              borderRadius: '50%',
              mx: 'auto',
              mb: 4,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: theme.tones.error.surface,
              color: theme.tones.error.main,
            })}
          >
            <ReportProblemOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>

          <Typography variant="h4" component="h1">
            Si è verificato un errore
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            L’interfaccia ha riscontrato un problema inatteso. Ricaricando la pagina dovresti poter continuare.
          </Typography>

          {import.meta.env.DEV && (
            <Box
              component="pre"
              sx={{
                mt: 4,
                p: 3,
                textAlign: 'left',
                fontSize: '0.75rem',
                overflowX: 'auto',
                borderRadius: 1.5,
                backgroundColor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {error.message}
            </Box>
          )}

          <Stack direction="row" sx={{ mt: 5, justifyContent: 'center' }}>
            <Button onClick={this.handleReload} variant="contained" startIcon={<RefreshIcon />}>
              Ricarica la pagina
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }
}
